// ENG-3353 — extension requests, as decided on 2026-09-23: approving allows the extension for the
// whole organization, a denied extension can be approved only by taking it off the deny list, and the
// member who asked sees the answer.
//
// Pure and React-free, like `extensionPolicy.ts`, so the rules are assertable without a render.

import { SubscriptionStatus } from "../types";
import { formatDate, parseDate } from "./format";
import { isAdminRole, isFullCapabilityTenantType, isProviderRole } from "./tenantAccess";

/**
 * ENG-3412 — may this person use the member request form?
 *
 * A member of a business or premium organization, WHETHER OR NOT its licence is active. This used to
 * require an active licence as well, and that left a lapsed organization's members locked down with
 * no way to ask:
 *
 *  - the lockdown does not lapse. `/subscription-status` resolves a member's policy from their
 *    organization regardless of billing (ENG-2662, a security control rather than a paid feature),
 *    and the extension applies `whitelistedExtensionIds` / `deniedExtensionIds` without reading any
 *    plan or subscription field;
 *  - `POST /business/extension-requests` does not check the licence either, and the extension's own
 *    request button (for a denied extension) already posts to it for these members.
 *
 * So the console was the one place that treated the licence as ending the conversation.
 *
 * Never an owner or admin, licensed or not. A licensed one has the allow list itself, and a lapsed
 * one would be asking themselves: the licence used to be what kept them off this form, so the role
 * has to do it now.
 *
 * ENG-3407: nor a provider's operator. They reach a client through a grant, not a membership, so the
 * form would file a request as someone the organization has no member for — and a Read-only tier is
 * refused the POST anyway. Their own organization's form is theirs when they are viewing it.
 */
export const mayRequestExtension = (status: SubscriptionStatus | null | undefined): boolean =>
  !!status?.role && !isAdminRole(status.role) && !isProviderRole(status.role) && isFullCapabilityTenantType(status.tenantType);

/**
 * ENG-3412 — a request from this member reaches admins who cannot review it in the console yet,
 * because the organization's licence is inactive and a lapsed admin has no console (deriveTenantAdmin).
 * They are still emailed, so the form says both halves instead of promising a review.
 *
 * The same active-licence test as `deriveTenantAdmin`, so the two cannot disagree about which
 * organizations are lapsed.
 */
export const requestAwaitsRenewal = (status: SubscriptionStatus | null | undefined): boolean =>
  !!status?.role && !(status.hasSubscription && status.status === "active");

/**
 * The backend's refusal code when a request names an extension on the organization's deny list —
 * `ExtensionRequestConflictResponse.ON_DENY_LIST` in the backend's RoutingModels.kt. Change both or
 * neither.
 */
export const EXTENSION_ON_DENY_LIST = "extension_on_deny_list";

/**
 * ENG-3399 — the backend's refusals when a member files a request, by `code`. Each is an answer, not a
 * failure: the member already asked (409), an admin declined it less than a day ago, or the member has
 * sent the day's maximum (both 429, `ExtensionRequestThrottledResponse` in the backend's
 * RoutingModels.kt). Change both or neither.
 */
export const REQUEST_ALREADY_PENDING = "request_already_pending";
export const REQUEST_RECENTLY_DECLINED = "request_recently_declined";
export const REQUEST_DAILY_LIMIT = "request_daily_limit";

/** What filing a member's request came to. `code` is the backend's, when it refused with one. */
export interface SubmitRequestResult {
  success: boolean;
  error?: string;
  code?: string;
}

export type SubmitRequest = (name: string, chromeExtensionId: string, reason: string) => Promise<SubmitRequestResult>;

/**
 * Whether a refusal is said as information rather than as an error, as the extension's pill does:
 * none of these is something the member got wrong, and a red banner for "you already asked" reads as
 * "it didn't work", which invites asking again.
 */
export const isAnswerNotFailure = (code: string | null | undefined): boolean =>
  code === REQUEST_ALREADY_PENDING || code === REQUEST_RECENTLY_DECLINED || code === REQUEST_DAILY_LIMIT;

/** Approving was refused because of the deny list: what taking it off the list would undo. */
export interface DenyConflict {
  extensionName: string;
  /** Who put it on the deny list. Null from a backend that does not say. */
  deniedBy: string | null;
  denyReason: string | null;
}

/**
 * Reads approve's 409 body. Null for any other refusal — another admin deciding it first carries no
 * code — which is then shown as the error it is. A body without the code is from a backend older
 * than this, and is treated the same way: its sentence already says what to do.
 */
export const denyConflictFrom = (body: unknown, fallbackName: string): DenyConflict | null => {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (b.code !== EXTENSION_ON_DENY_LIST) return null;
  const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
  return {
    extensionName: text(b.extensionName) ?? fallbackName,
    deniedBy: text(b.deniedBy),
    denyReason: text(b.denyReason),
  };
};

/**
 * A request's status as a member reads it. The route and the stored value say "denied", but the
 * admin's button says "Decline" (BlacklistSection's Deny means the opposite thing on the same page),
 * so the member is told the word their admin pressed. Anything unrecognised is "unknown" rather than
 * a guess.
 */
export type MemberRequestStatus = "pending" | "approved" | "declined" | "unknown";

export const memberRequestStatus = (status: string | null | undefined): MemberRequestStatus => {
  switch (status) {
    case "pending":
      return "pending";
    case "approved":
      return "approved";
    case "denied":
      return "declined";
    default:
      return "unknown";
  }
};

export const MEMBER_REQUEST_STATUS_LABEL: Record<MemberRequestStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  declined: "Declined",
  unknown: "Status unavailable",
};

/**
 * The calendar date of a backend timestamp, as "Sep 3, 2026". The backend sends a zone-less local
 * date-time; `formatDate` (lib/format.ts) reads its leading Y/M/D and drops the time, so the date
 * shown is exactly what the backend sent, in every viewer's zone. Null when there is no date to show.
 */
export const formatRequestDate = (raw: string | null | undefined): string | null =>
  raw && !Number.isNaN(parseDate(raw).getTime()) ? formatDate(raw) : null;
